import {
  TIPOS_DE_COMPARATIVO,
  type Cifras,
  type Comparativo,
  type Proyeccion,
  type TipoDeComparativo,
} from '@cnc/contracts';
import type {ReactNode} from 'react';
import {Link, useSearchParams} from 'react-router';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {useComparativo, useExportacionesDelComparativo, type FiltrosDelComparativo} from '../api/inventario';
import {localeActual, tr} from '../i18n/idioma';
import {fecha, numero} from '../utilidades/formato';
import {AYUDA_COMPARATIVO as A, AYUDA_MEDIDA_COMPARATIVO} from './ayuda-general';
import {CajaAjustable} from './caja-ajustable';
import {NotaDeAyuda} from './nota-de-ayuda';
import {SelectorMultiple} from './selector-multiple';

// Colores de las dos series (revisados para daltonismo y contraste):
// el año anterior en dorado, el elegido en verde.
const COLOR_ANTERIOR = '#c9963f';
const COLOR_ACTUAL = '#8a5a2b';
// La proyeccion, en azul con borde punteado (no es un dato real); distinta del dorado del año anterior.
const COLOR_PROYECCION = '#4a78a8';

type Mirada = 'exportado' | 'empacado';
type Medida = keyof Cifras;
const MEDIDAS: Medida[] = ['exportaciones', 'contenedores', 'bigBags', 'tonSecas', 'tonHumedas', 'auOz', 'agOz'];
// Solo existen en lo exportado (no en lo empacado).
const SOLO_EXPORTADO = new Set<Medida>(['exportaciones', 'contenedores']);
const nombreMedida = (m: Medida) =>
  ({
    exportaciones: tr('Exportaciones', 'Exports'),
    contenedores: tr('Contenedores', 'Containers'),
    bigBags: 'Big Bags',
    tonSecas: tr('Toneladas secas', 'Dry tons'),
    tonHumedas: tr('Toneladas húmedas', 'Wet tons'),
    auOz: tr('Oro (oz)', 'Gold (oz)'),
    agOz: tr('Plata (oz)', 'Silver (oz)'),
  })[m];
const decimalesDe = (m: Medida) => (m === 'exportaciones' || m === 'contenedores' || m === 'bigBags' ? 0 : 2);
const HORIZONTES = ['3', '6', '9', '12'] as const;

// Variacion porcentual; null si no hay base para comparar.
const variacion = (actual: number, base: number) => (base > 0 ? (actual - base) / base : null);

// Año contra año en el tablero de inicio: lo que va del año elegido frente al
// mismo periodo del anterior y frente al año anterior completo; por mes y por
// exportador. Los filtros viven en la direccion (se pueden guardar o compartir).
export function ComparativoAnual() {
  const [parametros, setParametros] = useSearchParams();
  const anio = Number(parametros.get('anio')) || undefined;
  // Año contra el que se compara (6/10/2026); sin el, el anterior.
  const anioComparar = Number(parametros.get('anioComparar')) || undefined;
  const exportador = parametros.get('exportador') ?? '';
  const tipo = (TIPOS_DE_COMPARATIVO as readonly string[]).includes(parametros.get('tipo') ?? '')
    ? (parametros.get('tipo') as TipoDeComparativo)
    : 'propias';
  const mirada: Mirada = parametros.get('mirada') === 'empacado' ? 'empacado' : 'exportado';
  const medidaPedida = parametros.get('medida') as Medida | null;
  const medida: Medida =
    medidaPedida && MEDIDAS.includes(medidaPedida) && !(mirada === 'empacado' && SOLO_EXPORTADO.has(medidaPedida))
      ? medidaPedida
      : mirada === 'exportado'
        ? 'exportaciones'
        : 'bigBags';
  const acumulado = parametros.get('vista') === 'acumulado';
  const proveedor = parametros.get('proveedor') ?? '';
  const mina = parametros.get('mina') ?? '';
  const mesesEtd = parametros.get('mesesEtd') ?? '';
  // Proyeccion: solo se calcula y se ve al apretar el boton (?proyeccion=3|6|9|12).
  const proyeccionPedida = parametros.get('proyeccion');
  const proyeccion = (HORIZONTES as readonly string[]).includes(proyeccionPedida ?? '')
    ? (proyeccionPedida as (typeof HORIZONTES)[number])
    : undefined;
  const filtros = {
    anio,
    anioComparar,
    tipo,
    exportador: exportador || undefined,
    proveedor: proveedor || undefined,
    mina: mina || undefined,
    mesesEtd: mesesEtd || undefined,
    proyeccion,
  };
  const nombreLargoDelMes = new Intl.DateTimeFormat(localeActual(), {month: 'long'});
  const opcionesDeMes = Array.from({length: 12}, (_, i) => {
    const nombre = nombreLargoDelMes.format(new Date(Date.UTC(2000, i, 15)));
    return {valor: String(i + 1), etiqueta: nombre.charAt(0).toUpperCase() + nombre.slice(1)};
  });
  const consulta = useComparativo(filtros);
  // Barras elegidas en la grafica ("2026-03|2025-07"): filtran la lista de exportaciones.
  const elegidas = (parametros.get('meses') ?? '').split('|').filter(Boolean);
  const cambiar = (clave: string, valor: string | null) => {
    const nuevos = new URLSearchParams(parametros);
    if (valor === null || valor === '') nuevos.delete(clave);
    else nuevos.set(clave, valor);
    setParametros(nuevos, {replace: true});
  };

  const c = consulta.data;
  const campo = 'rounded-md border border-linea bg-white px-2 py-1 text-sm';
  return (
    <section
      className={consulta.isFetching && c ? 'opacity-70' : ''}
      aria-label={tr('Año contra año', 'Year over year')}
    >
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold tracking-wide text-apagado uppercase">
            {c
              ? tr(`${c.anio} frente a ${c.anioAnterior}`, `${c.anio} vs ${c.anioAnterior}`)
              : tr('Año contra año', 'Year over year')}
            <NotaDeAyuda texto={A.tarjetas} />
          </h2>
          {c && (
            <p className="text-xs text-apagado">
              {c.enCurso
                ? tr(
                    `Del 1 de enero al ${fecha(c.actual.hasta)} frente al mismo periodo de ${c.anioAnterior} y a todo ${c.anioAnterior}.`,
                    `From January 1 to ${fecha(c.actual.hasta)} versus the same period of ${c.anioAnterior} and all of ${c.anioAnterior}.`,
                  )
                : tr(
                    `Año ${c.anio} completo frente a ${c.anioAnterior}.`,
                    `Full year ${c.anio} versus ${c.anioAnterior}.`,
                  )}{' '}
              {mirada === 'exportado'
                ? tr(
                    'Exportaciones por fecha de zarpe (ETD), sin las canceladas.',
                    'Exports by departure date (ETD), excluding cancelled ones.',
                  )
                : tr('Big Bags por fecha de empaque.', 'Big Bags by packing date.')}
            </p>
          )}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-end gap-2 rounded-lg border border-linea bg-white px-3 py-2 text-sm">
        <label className="flex flex-col gap-1 text-xs text-apagado">
          <span>
            {tr('Año', 'Year')}
            <NotaDeAyuda texto={A.anio} dentroDeEtiqueta />
          </span>
          <select className={campo} value={c?.anio ?? ''} onChange={e => cambiar('anio', e.target.value)}>
            {(c?.anios ?? []).map(a => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-apagado">
          <span>
            {tr('Comparar con', 'Compare with')}
            <NotaDeAyuda texto={A.comparar} dentroDeEtiqueta />
          </span>
          <select
            className={campo}
            value={c?.anioAnterior ?? ''}
            onChange={e =>
              // El anterior es el de siempre: no se deja en la direccion.
              cambiar('anioComparar', c && Number(e.target.value) === c.anio - 1 ? null : e.target.value)
            }
          >
            {[...new Set([...(c?.anios ?? []), ...(c ? [c.anio - 1, c.anioAnterior] : [])])]
              .filter(a => a !== c?.anio)
              .sort((x, y) => y - x)
              .map(a => (
                <option key={a} value={a}>
                  {a}
                  {c && a === c.anio - 1 ? tr(' (anterior)', ' (previous)') : ''}
                </option>
              ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-apagado">
          <span>
            {tr('Exportaciones', 'Exports')}
            <NotaDeAyuda texto={A.tipo} dentroDeEtiqueta />
          </span>
          <select className={campo} value={tipo} onChange={e => cambiar('tipo', e.target.value)}>
            <option value="propias">{tr('Propias', 'Own')}</option>
            <option value="terceros">{tr('De terceros', 'Third party')}</option>
            <option value="mineros">Mineros</option>
            <option value="todas">{tr('Todas', 'All')}</option>
          </select>
        </label>
        <SelectorMultiple
          etiqueta={tr('Exportador', 'Exporter')}
          valor={exportador || undefined}
          opciones={(c?.exportadores ?? []).map(x => ({
            valor: x,
            etiqueta: x === 'Third Party' ? tr('De terceros', 'Third party') : x,
          }))}
          onCambiar={v => cambiar('exportador', v ?? null)}
          ayuda={A.exportador}
          clase="flex w-44 flex-col gap-1 text-xs text-apagado"
        />
        <SelectorMultiple
          etiqueta={tr('Proveedor', 'Supplier')}
          valor={proveedor || undefined}
          opciones={(c?.proveedores ?? []).map(x => ({valor: x, etiqueta: x}))}
          onCambiar={v => cambiar('proveedor', v ?? null)}
          ayuda={A.proveedor}
          clase="flex w-44 flex-col gap-1 text-xs text-apagado"
        />
        <SelectorMultiple
          etiqueta={tr('Mina', 'Mine')}
          valor={mina || undefined}
          opciones={(c?.minas ?? []).map(x => ({valor: x, etiqueta: x}))}
          onCambiar={v => cambiar('mina', v ?? null)}
          ayuda={A.mina}
          clase="flex w-44 flex-col gap-1 text-xs text-apagado"
        />
        {/* Meses del año: con Exportado, por mes de zarpe (ETD); con Empacado, por mes de empaque. */}
        <SelectorMultiple
          etiqueta={
            mirada === 'exportado'
              ? tr('Mes de zarpe (ETD)', 'Departure month (ETD)')
              : tr('Mes de empaque', 'Packing month')
          }
          valor={mesesEtd || undefined}
          opciones={opcionesDeMes}
          onCambiar={v => cambiar('mesesEtd', v ?? null)}
          ayuda={A.meses}
          clase="flex w-44 flex-col gap-1 text-xs text-apagado"
        />
        <div className="flex gap-1" role="group" aria-label={tr('Qué contar', 'What to count')}>
          {(
            [
              ['exportado', tr('Exportado', 'Exported')],
              ['empacado', tr('Empacado', 'Packed')],
            ] as const
          ).map(([clave, texto]) => (
            <button
              key={clave}
              type="button"
              aria-pressed={mirada === clave}
              onClick={() => cambiar('mirada', clave === 'exportado' ? null : clave)}
              className={`rounded-full border px-3 py-1 ${mirada === clave ? 'border-verde bg-verde-claro text-verde-oscuro' : 'border-linea bg-white text-apagado'}`}
            >
              {texto}
            </button>
          ))}
          <span className="self-center">
            <NotaDeAyuda titulo={tr('Qué contar', 'What to count')} texto={A.mirada} />
          </span>
        </div>
        {/* Proyeccion: solo se ve al apretar el boton; luego se elige el horizonte. */}
        <div className="flex items-center gap-1" role="group" aria-label={tr('Proyección', 'Projection')}>
          <button
            type="button"
            aria-pressed={!!proyeccion}
            onClick={() => cambiar('proyeccion', proyeccion ? null : '3')}
            className={`rounded-full border px-3 py-1 ${proyeccion ? 'border-dorado bg-dorado-claro text-tinta' : 'border-linea bg-white text-apagado'}`}
          >
            📈 {tr('Proyección', 'Projection')}
          </button>
          {proyeccion &&
            HORIZONTES.map(h => (
              <button
                key={h}
                type="button"
                aria-pressed={proyeccion === h}
                onClick={() => cambiar('proyeccion', h)}
                className={`rounded-full border px-2.5 py-1 text-xs ${proyeccion === h ? 'border-dorado bg-dorado-claro text-tinta' : 'border-linea bg-white text-apagado'}`}
              >
                {tr(`${h} meses`, `${h} months`)}
              </button>
            ))}
          <NotaDeAyuda titulo={tr('Proyección', 'Projection')} texto={A.proyeccion} />
        </div>
      </div>

      {consulta.isError && (
        <p className="mt-3 text-sm text-alerta">
          {consulta.error.message} {/* Un año o filtro invalido en la direccion no debe dejar el inicio bloqueado. */}
          <button
            type="button"
            onClick={() => {
              const nuevos = new URLSearchParams(parametros);
              for (const clave of ['anio', 'anioComparar', 'mesesEtd', 'meses', 'proyeccion']) nuevos.delete(clave);
              setParametros(nuevos, {replace: true});
            }}
            className="ml-1 text-verde underline"
          >
            {tr('Volver al año actual', 'Back to the current year')}
          </button>
        </p>
      )}
      {c && proyeccion && !c.enCurso && (
        <p className="mt-3 text-sm text-apagado">
          {tr(
            'La proyección se calcula sobre el año en curso: elige el año actual para verla.',
            'The projection is calculated for the current year: choose the current year to see it.',
          )}
        </p>
      )}
      {c && (
        <>
          <Tarjetas c={c} mirada={mirada} medida={medida} onElegir={m => cambiar('medida', m)} />
          <Grafica
            c={c}
            mirada={mirada}
            medida={medida}
            acumulado={acumulado}
            onVista={v => cambiar('vista', v)}
            elegidas={elegidas}
            onElegir={meses => cambiar('meses', meses.join('|') || null)}
          />
          {c.proyeccion && (
            <TablaDeProyeccion p={c.proyeccion} anio={c.anio} comparadoCon={c.anioAnterior} mirada={mirada} />
          )}
          <ExportacionesDeLaGrafica
            filtros={{...filtros, anio: c.anio, proyeccion: undefined}}
            meses={elegidas}
            empacado={mirada === 'empacado'}
            onQuitar={() => cambiar('meses', null)}
          />
          <TablaPorExportador c={c} mirada={mirada} medida={medida} />
        </>
      )}
    </section>
  );
}

function Variacion({valor}: {valor: number | null}) {
  if (valor === null) return <span className="text-apagado">—</span>;
  const sube = valor >= 0;
  return (
    <span className={sube ? 'text-verde-oscuro' : 'text-alerta'}>
      {sube ? '▲' : '▼'} {sube ? '+' : ''}
      {numero(valor * 100, 1)} %
    </span>
  );
}

function Renglon({titulo, valor}: {titulo: string; valor: ReactNode}) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-apagado">{titulo}</dt>
      <dd className="font-medium whitespace-nowrap text-tinta tabular-nums">{valor}</dd>
    </div>
  );
}

function Tarjetas({
  c,
  mirada,
  medida,
  onElegir,
}: {
  c: Comparativo;
  mirada: Mirada;
  medida: Medida;
  onElegir: (m: Medida) => void;
}) {
  const medidas = MEDIDAS.filter(m => m !== 'tonHumedas' && !(mirada === 'empacado' && SOLO_EXPORTADO.has(m)));
  const etiquetaActual = c.enCurso ? tr(`${c.anio} hasta hoy`, `${c.anio} to date`) : String(c.anio);
  const p = c.proyeccion;
  return (
    <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
      {medidas.map(m => {
        const actual = c.actual[mirada][m];
        const mismo = c.mismoPeriodoAnterior[mirada][m];
        const completo = c.anteriorCompleto[mirada][m];
        const elegida = m === medida;
        // La nota va fuera del boton de la tarjeta (un boton no puede ir dentro de otro).
        return (
          <div key={m} className="relative">
            <span className="absolute top-3 right-3 z-10">
              <NotaDeAyuda titulo={nombreMedida(m)} texto={AYUDA_MEDIDA_COMPARATIVO[m]!} />
            </span>
            <button
              type="button"
              onClick={() => onElegir(m)}
              aria-pressed={elegida}
              className={`h-full w-full rounded-lg border bg-white p-4 text-left ${elegida ? 'border-verde ring-1 ring-verde' : 'border-linea hover:border-verde/60'}`}
            >
              <div className="pr-5 text-xs font-semibold tracking-wide text-apagado uppercase">{nombreMedida(m)}</div>
              <div className="mt-1 text-2xl font-semibold text-verde-oscuro tabular-nums">
                {numero(actual, decimalesDe(m))}
              </div>
              <div className="text-xs text-apagado">{etiquetaActual}</div>
              <dl className="mt-3 space-y-0.5 text-xs">
                {c.enCurso && (
                  <>
                    <Renglon
                      titulo={tr(`Mismo periodo ${c.anioAnterior}`, `Same period ${c.anioAnterior}`)}
                      valor={numero(mismo, decimalesDe(m))}
                    />
                    <Renglon
                      titulo={tr('Variación', 'Change')}
                      valor={<Variacion valor={variacion(actual, mismo)} />}
                    />
                  </>
                )}
                <Renglon
                  titulo={tr(`${c.anioAnterior} completo`, `Full ${c.anioAnterior}`)}
                  valor={numero(completo, decimalesDe(m))}
                />
                {c.enCurso ? (
                  <Renglon
                    titulo={tr(`Avance frente a ${c.anioAnterior}`, `Reached vs ${c.anioAnterior}`)}
                    valor={completo > 0 ? `${numero((actual / completo) * 100, 0)} %` : '—'}
                  />
                ) : (
                  <Renglon
                    titulo={tr('Variación', 'Change')}
                    valor={<Variacion valor={variacion(actual, completo)} />}
                  />
                )}
              </dl>
              {p && (
                <dl className="mt-2 space-y-0.5 border-t border-dashed border-dorado pt-2 text-xs">
                  <Renglon
                    titulo={tr(`Proyección ${p.meses} meses`, `${p.meses}-month projection`)}
                    valor={numero(p.total[mirada][m], decimalesDe(m))}
                  />
                  {p.cierreDelAnio && (
                    <Renglon
                      titulo={tr(`Cierre ${c.anio} proyectado`, `Projected ${c.anio} close`)}
                      valor={numero(p.cierreDelAnio[mirada][m], decimalesDe(m))}
                    />
                  )}
                </dl>
              )}
            </button>
          </div>
        );
      })}
    </div>
  );
}

const claveDeMes = (anio: number, indice: number) => `${anio}-${String(indice + 1).padStart(2, '0')}`;

function Grafica({
  c,
  mirada,
  medida,
  acumulado,
  onVista,
  elegidas,
  onElegir,
}: {
  c: Comparativo;
  mirada: Mirada;
  medida: Medida;
  acumulado: boolean;
  onVista: (v: string | null) => void;
  // Meses elegidos ("2026-03"): filtran la lista de exportaciones.
  elegidas: string[];
  onElegir: (meses: string[]) => void;
}) {
  // Clic: solo esa barra (otro clic en la misma la suelta). Control + clic
  // (Cmd en Mac): la agrega o la quita de las elegidas.
  const elegir = (anio: number, indice: number, varias: boolean) => {
    const clave = claveDeMes(anio, indice);
    const ya = elegidas.includes(clave);
    if (varias) onElegir(ya ? elegidas.filter(x => x !== clave) : [...elegidas, clave].sort());
    else onElegir(ya && elegidas.length === 1 ? [] : [clave]);
  };
  const opacidad = (anio: number, indice: number) =>
    !elegidas.length || elegidas.includes(claveDeMes(anio, indice)) ? 1 : 0.35;
  const conControl = (evento: unknown) => {
    const e = evento as {ctrlKey?: boolean; metaKey?: boolean} | undefined;
    return Boolean(e?.ctrlKey || e?.metaKey);
  };
  const nombreDelMes = new Intl.DateTimeFormat(localeActual(), {month: 'short'});
  const mesActual = c.enCurso ? Number(c.hoy.slice(5, 7)) : 12;
  let sumaActual = 0;
  let sumaAnterior = 0;
  // Proyeccion de los meses de este año que faltan (si se pidio).
  const proyectadoDe = new Map(
    (c.proyeccion?.porMes ?? [])
      .filter(x => x.mes.startsWith(String(c.anio)))
      .map(x => [Number(x.mes.slice(5, 7)), x.proyectado[mirada][medida]]),
  );
  let sumaProyectada = 0;
  const datos: Array<{
    mes: string;
    anterior: number | null;
    actual: number | null;
    proyeccion: number | null;
    proyeccionSiguiente?: number | null;
  }> = c.porMes.map(m => {
    sumaActual += m.actual[mirada][medida];
    sumaAnterior += m.anterior[mirada][medida];
    const proyectado = proyectadoDe.get(m.mes);
    if (proyectado !== undefined) sumaProyectada += proyectado;
    return {
      mes: nombreDelMes.format(new Date(Date.UTC(2000, m.mes - 1, 15))).replace('.', ''),
      anterior: acumulado ? sumaAnterior : m.anterior[mirada][medida],
      // Los meses que no han llegado quedan vacios (no en cero).
      actual: m.mes > mesActual ? null : acumulado ? sumaActual : m.actual[mirada][medida],
      // Acumulada, la proyeccion arranca donde va el año (mes de hoy) y sigue.
      proyeccion:
        proyectado !== undefined
          ? acumulado
            ? sumaActual + sumaProyectada
            : proyectado
          : acumulado && proyectadoDe.size && m.mes === mesActual
            ? sumaActual
            : null,
    };
  });
  // La proyeccion que pasa de diciembre (Daniel, 9/10/2026: no se veia 2027):
  // la grafica sigue con los meses del año siguiente, con su año en la
  // etiqueta y una linea que separa los dos años. Acumulada, vuelve a empezar
  // en enero, como el año, en un tramo aparte (sin bajar desde diciembre).
  const siguientes = (c.proyeccion?.porMes ?? []).filter(x => !x.mes.startsWith(String(c.anio)));
  const primerMesSiguiente = siguientes.length ? etiquetaConAnio(siguientes[0]!.mes, nombreDelMes) : null;
  let anioDeLaSuma = c.anio;
  let sumaSiguiente = 0;
  for (const x of siguientes) {
    const anio = Number(x.mes.slice(0, 4));
    if (anio !== anioDeLaSuma) {
      anioDeLaSuma = anio;
      sumaSiguiente = 0;
    }
    const valor = x.proyectado[mirada][medida];
    sumaSiguiente += valor;
    datos.push({
      mes: etiquetaConAnio(x.mes, nombreDelMes),
      anterior: null,
      actual: null,
      proyeccion: acumulado ? null : valor,
      proyeccionSiguiente: acumulado ? sumaSiguiente : null,
    });
  }
  const conProyeccion = proyectadoDe.size > 0 || siguientes.length > 0;
  const separadorDeAnio = primerMesSiguiente && (
    <ReferenceLine
      x={primerMesSiguiente}
      stroke="var(--color-apagado)"
      strokeDasharray="3 3"
      label={{
        value: siguientes[0]!.mes.slice(0, 4),
        position: 'insideTopLeft',
        fill: 'var(--color-apagado)',
        fontSize: 12,
      }}
    />
  );
  const serieProyeccion = tr('Proyección', 'Projection');
  const formato = (v: unknown) => (typeof v === 'number' ? numero(v, decimalesDe(medida)) : '—');
  // Valor sobre cada barra: corto (1,2 mil) para que quepa; sin etiqueta en cero.
  const corto = new Intl.NumberFormat(localeActual(), {
    notation: 'compact',
    maximumFractionDigits: decimalesDe(medida) ? 1 : 0,
  });
  const etiquetaDeBarra = (v: unknown) => (typeof v === 'number' && v > 0 ? corto.format(v) : '');
  const serieAnterior = String(c.anioAnterior);
  const serieActual = c.enCurso ? tr(`${c.anio} (hasta hoy)`, `${c.anio} (to date)`) : String(c.anio);
  return (
    <div className="mt-4 rounded-lg border border-linea bg-white p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h3 className="font-medium">
            {nombreMedida(medida)}{' '}
            {acumulado ? tr('acumuladas por mes', 'cumulative by month') : tr('por mes', 'by month')}
          </h3>
          {/* Totales de lo que muestra la grafica, con los filtros puestos. */}
          <p className="text-xs text-apagado" data-prueba="totales-de-la-grafica">
            {tr('Total', 'Total')} {serieAnterior}:{' '}
            <b className="text-tinta tabular-nums">{numero(sumaAnterior, decimalesDe(medida))}</b> · {serieActual}:{' '}
            <b className="text-tinta tabular-nums">{numero(sumaActual, decimalesDe(medida))}</b>
          </p>
        </div>
        <div className="flex gap-1 text-xs" role="group" aria-label={tr('Vista de la gráfica', 'Chart view')}>
          {(
            [
              [null, tr('Mensual', 'Monthly')],
              ['acumulado', tr('Acumulado', 'Cumulative')],
            ] as const
          ).map(([clave, texto]) => (
            <button
              key={texto}
              type="button"
              aria-pressed={(clave === 'acumulado') === acumulado}
              onClick={() => onVista(clave)}
              className={`rounded-full border px-3 py-1 ${(clave === 'acumulado') === acumulado ? 'border-verde bg-verde-claro text-verde-oscuro' : 'border-linea bg-white text-apagado'}`}
            >
              {texto}
            </button>
          ))}
          <span className="self-center">
            <NotaDeAyuda titulo={tr('Vista de la gráfica', 'Chart view')} texto={A.vista} />
          </span>
        </div>
      </div>
      <div className="mt-2 h-80">
        <ResponsiveContainer width="100%" height="100%">
          {acumulado ? (
            <LineChart data={datos} margin={{top: 8, right: 16, left: 0, bottom: 0}}>
              <CartesianGrid stroke="var(--color-linea)" vertical={false} />
              <XAxis
                dataKey="mes"
                tick={{fontSize: 12, fill: 'var(--color-apagado)'}}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tick={{fontSize: 12, fill: 'var(--color-apagado)'}}
                tickLine={false}
                axisLine={false}
                tickFormatter={formato}
                allowDecimals={decimalesDe(medida) > 0}
                width={70}
              />
              <Tooltip formatter={formato} />
              <Legend />
              {separadorDeAnio}
              <Line
                type="monotone"
                dataKey="anterior"
                name={serieAnterior}
                stroke={COLOR_ANTERIOR}
                strokeWidth={2}
                dot={{r: 4}}
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="actual"
                name={serieActual}
                stroke={COLOR_ACTUAL}
                strokeWidth={2}
                dot={{r: 4}}
                connectNulls={false}
                isAnimationActive={false}
              />
              {conProyeccion && (
                <Line
                  type="monotone"
                  dataKey="proyeccion"
                  name={serieProyeccion}
                  stroke={COLOR_PROYECCION}
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  dot={{r: 3}}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              )}
              {primerMesSiguiente && (
                <Line
                  type="monotone"
                  dataKey="proyeccionSiguiente"
                  name={serieProyeccion}
                  legendType="none"
                  stroke={COLOR_PROYECCION}
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  dot={{r: 3}}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              )}
            </LineChart>
          ) : (
            <BarChart data={datos} margin={{top: 18, right: 16, left: 0, bottom: 0}} barGap={2}>
              <CartesianGrid stroke="var(--color-linea)" vertical={false} />
              <XAxis
                dataKey="mes"
                tick={{fontSize: 12, fill: 'var(--color-apagado)'}}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tick={{fontSize: 12, fill: 'var(--color-apagado)'}}
                tickLine={false}
                axisLine={false}
                tickFormatter={formato}
                allowDecimals={decimalesDe(medida) > 0}
                width={70}
              />
              <Tooltip
                formatter={formato}
                cursor={{fill: 'var(--color-papel)'}}
                contentStyle={{background: 'var(--color-white)', borderColor: 'var(--color-linea)'}}
              />
              <Legend />
              {separadorDeAnio}
              <Bar
                dataKey="anterior"
                name={serieAnterior}
                fill={COLOR_ANTERIOR}
                radius={[4, 4, 0, 0]}
                maxBarSize={22}
                isAnimationActive={false}
                cursor="pointer"
                onClick={(_, indice, evento) => elegir(c.anioAnterior, indice, conControl(evento))}
              >
                {datos.map((_, i) => (
                  <Cell key={i} fillOpacity={opacidad(c.anioAnterior, i)} />
                ))}
                <LabelList
                  dataKey="anterior"
                  position="top"
                  formatter={etiquetaDeBarra}
                  style={{fontSize: 10, fill: 'var(--color-apagado)'}}
                />
              </Bar>
              <Bar
                dataKey="actual"
                name={serieActual}
                fill={COLOR_ACTUAL}
                radius={[4, 4, 0, 0]}
                maxBarSize={22}
                isAnimationActive={false}
                cursor="pointer"
                onClick={(_, indice, evento) => elegir(c.anio, indice, conControl(evento))}
              >
                {datos.map((_, i) => (
                  <Cell key={i} fillOpacity={opacidad(c.anio, i)} />
                ))}
                <LabelList
                  dataKey="actual"
                  position="top"
                  formatter={etiquetaDeBarra}
                  style={{fontSize: 10, fill: 'var(--color-tinta)'}}
                />
              </Bar>
              {conProyeccion && (
                <Bar
                  dataKey="proyeccion"
                  name={serieProyeccion}
                  fill={COLOR_PROYECCION}
                  fillOpacity={0.35}
                  stroke={COLOR_PROYECCION}
                  strokeDasharray="4 3"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={22}
                  isAnimationActive={false}
                >
                  <LabelList
                    dataKey="proyeccion"
                    position="top"
                    formatter={etiquetaDeBarra}
                    style={{fontSize: 10, fill: 'var(--color-tinta)', fontStyle: 'italic'}}
                  />
                </Bar>
              )}
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
      {!acumulado && (
        <p className="mt-1 text-xs text-apagado">
          {tr(
            'Clic en una barra filtra la lista de exportaciones de abajo; Control + clic elige varias barras.',
            'Click a bar to filter the export list below; Ctrl + click picks several bars.',
          )}
        </p>
      )}
    </div>
  );
}

// Las exportaciones de la grafica, siempre a la vista: las de las barras
// elegidas o, sin barras, todas las del periodo (por ETD, sin canceladas, con
// los mismos filtros).
function ExportacionesDeLaGrafica({
  filtros,
  meses,
  empacado,
  onQuitar,
}: {
  filtros: FiltrosDelComparativo;
  meses: string[];
  empacado: boolean;
  onQuitar: () => void;
}) {
  const consulta = useExportacionesDelComparativo({...filtros, meses: meses.join('|') || undefined});
  const nombreDelMes = new Intl.DateTimeFormat(localeActual(), {month: 'short', year: 'numeric'});
  const etiquetaDe = (clave: string) => {
    const [a, m] = clave.split('-').map(Number) as [number, number];
    return nombreDelMes.format(new Date(Date.UTC(a, m - 1, 15)));
  };
  const filas = consulta.data?.exportaciones ?? [];
  const suma = (campo: 'bigBags' | 'tonSecas' | 'auOz' | 'agOz') => filas.reduce((s, x) => s + x[campo], 0);
  return (
    <div
      className="mt-4 rounded-lg border border-linea bg-white p-4 text-sm"
      aria-label={tr('Exportaciones de la gráfica', 'Exports in the chart')}
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h3 className="font-medium">{tr('Exportaciones', 'Exports')}</h3>
        <span className="text-xs text-apagado">
          {meses.length
            ? tr(
                `Barras elegidas: ${meses.map(etiquetaDe).join(', ')}`,
                `Chosen bars: ${meses.map(etiquetaDe).join(', ')}`,
              )
            : consulta.data
              ? tr(
                  `Todas las del ${fecha(consulta.data.desde)} al ${fecha(consulta.data.hasta)}`,
                  `All from ${fecha(consulta.data.desde)} to ${fecha(consulta.data.hasta)}`,
                )
              : ''}
          {empacado &&
            tr(' · por fecha de zarpe (la gráfica está en Empacado)', ' · by departure date (the chart is on Packed)')}
        </span>
        {meses.length > 0 && (
          <button type="button" onClick={onQuitar} className="ml-auto text-xs text-apagado hover:text-tinta">
            {tr('✕ Quitar selección', '✕ Clear selection')}
          </button>
        )}
      </div>
      {consulta.isError && <p className="mt-3 text-alerta">{consulta.error.message}</p>}
      {consulta.data && filas.length === 0 && (
        <p className="mt-3 text-apagado">
          {tr('No hay exportaciones con estos filtros.', 'No exports with these filters.')}
        </p>
      )}
      {filas.length > 0 && (
        <CajaAjustable altoMaximo="24rem" className={`mt-2 overflow-auto ${consulta.isFetching ? 'opacity-70' : ''}`}>
          <table className="w-full">
            <thead className="sticky top-0 bg-white">
              <tr className="text-left text-xs tracking-wide text-apagado uppercase">
                <th className="py-1 pr-2 text-right font-semibold">
                  #<NotaDeAyuda texto={A.numero} />
                </th>
                <th className="py-1 font-semibold">
                  {tr('Exportación', 'Export')}
                  <NotaDeAyuda texto={A.codigo} />
                </th>
                <th className="py-1 font-semibold">
                  {tr('Exportador', 'Exporter')}
                  <NotaDeAyuda texto={A.exportador} />
                </th>
                <th className="py-1 font-semibold">
                  {tr('Estado', 'Status')}
                  <NotaDeAyuda texto={A.estado} />
                </th>
                <th className="py-1 font-semibold">
                  ETD
                  <NotaDeAyuda texto={A.etd} />
                </th>
                <th className="py-1 text-right font-semibold">
                  Big Bags
                  <NotaDeAyuda texto={A.listaBigBags} />
                </th>
                <th className="py-1 text-right font-semibold">
                  {tr('Ton. secas', 'Dry tons')}
                  <NotaDeAyuda texto={A.listaTonSecas} />
                </th>
                <th className="py-1 text-right font-semibold">
                  {tr('Oro (oz)', 'Gold (oz)')}
                  <NotaDeAyuda texto={A.listaAuOz} />
                </th>
                <th className="py-1 text-right font-semibold">
                  {tr('Plata (oz)', 'Silver (oz)')}
                  <NotaDeAyuda texto={A.listaAgOz} />
                </th>
              </tr>
            </thead>
            <tbody>
              {filas.map((x, i) => (
                <tr key={x.codigo} className="border-t border-linea/70">
                  <td className="py-1 pr-2 text-right text-xs text-apagado tabular-nums">{i + 1}</td>
                  <td className="py-1">
                    <Link
                      to={`/seguimiento/${encodeURIComponent(x.codigo)}`}
                      className="font-medium text-verde hover:underline"
                    >
                      {x.codigo}
                    </Link>
                  </td>
                  <td className="py-1">
                    {x.exportador === 'Third Party' ? tr('De terceros', 'Third party') : x.exportador}
                  </td>
                  <td className="py-1">{x.estado}</td>
                  <td className="py-1 tabular-nums">{fecha(x.etd)}</td>
                  <td className="py-1 text-right tabular-nums">{numero(x.bigBags)}</td>
                  <td className="py-1 text-right tabular-nums">{numero(x.tonSecas, 2)}</td>
                  <td className="py-1 text-right tabular-nums">{numero(x.auOz, 2)}</td>
                  <td className="py-1 text-right tabular-nums">{numero(x.agOz, 2)}</td>
                </tr>
              ))}
              <tr className="border-t border-linea font-semibold">
                <td />
                <td className="py-1" colSpan={4}>
                  {tr(`${numero(filas.length)} exportaciones`, `${numero(filas.length)} exports`)}
                </td>
                <td className="py-1 text-right tabular-nums">{numero(suma('bigBags'))}</td>
                <td className="py-1 text-right tabular-nums">{numero(suma('tonSecas'), 2)}</td>
                <td className="py-1 text-right tabular-nums">{numero(suma('auOz'), 2)}</td>
                <td className="py-1 text-right tabular-nums">{numero(suma('agOz'), 2)}</td>
              </tr>
            </tbody>
          </table>
        </CajaAjustable>
      )}
    </div>
  );
}

// "2027-01" → "ene 27": mes corto con el año, para los meses proyectados que
// pasan de diciembre.
function etiquetaConAnio(clave: string, nombreDelMes: Intl.DateTimeFormat) {
  const [a, m] = clave.split('-').map(Number) as [number, number];
  return `${nombreDelMes.format(new Date(Date.UTC(2000, m - 1, 15))).replace('.', '')} ${String(a).slice(2)}`;
}

// La proyeccion mes a mes de cada medida, con su total y, si llega a
// diciembre, el cierre del año proyectado.
function TablaDeProyeccion({
  p,
  anio,
  comparadoCon,
  mirada,
}: {
  p: Proyeccion;
  anio: number;
  comparadoCon: number;
  mirada: Mirada;
}) {
  const medidas = MEDIDAS.filter(m => m !== 'tonHumedas' && !(mirada === 'empacado' && SOLO_EXPORTADO.has(m)));
  const nombreDelMes = new Intl.DateTimeFormat(localeActual(), {month: 'long', year: 'numeric'});
  const etiquetaDe = (clave: string) => {
    const [a, m] = clave.split('-').map(Number) as [number, number];
    const t = nombreDelMes.format(new Date(Date.UTC(a, m - 1, 15)));
    return t.charAt(0).toUpperCase() + t.slice(1);
  };
  const celda = 'py-1 text-right tabular-nums';
  return (
    <section
      className="mt-4 overflow-x-auto rounded-lg border border-dashed border-dorado bg-white p-4 text-sm"
      aria-label={tr('Proyección', 'Projection')}
    >
      <h3 className="font-medium">
        📈 {tr(`Proyección de los próximos ${p.meses} meses`, `Projection for the next ${p.meses} months`)}
      </h3>
      <p className="text-xs text-apagado">
        {p.mesesCerrados > 0
          ? tr(
              `Cada mes: la mitad el ritmo mensual de ${anio} (promedio de los ${p.mesesCerrados} meses ya cerrados) y la mitad lo que pasó ese mismo mes un año antes. Con los filtros puestos. Es una estimación, no un dato real.`,
              `Each month: half the monthly pace of ${anio} (average of the ${p.mesesCerrados} closed months) and half what happened in that same month a year earlier. With the current filters. It is an estimate, not actual data.`,
            )
          : tr(
              `Cada mes: la mitad el promedio mensual de ${anio - 1} (aún no hay meses cerrados en ${anio}) y la mitad lo que pasó ese mismo mes un año antes. Con los filtros puestos. Es una estimación, no un dato real.`,
              `Each month: half the monthly average of ${anio - 1} (no closed months in ${anio} yet) and half what happened in that same month a year earlier. With the current filters. It is an estimate, not actual data.`,
            )}
        {comparadoCon !== anio - 1 &&
          tr(
            ` Como se compara con ${comparadoCon} (no con el año anterior), los meses de ${anio} se proyectan solo con su ritmo.`,
            ` Since the comparison is with ${comparadoCon} (not the previous year), the months of ${anio} are projected with their pace only.`,
          )}
      </p>
      <table className="mt-2 w-full">
        <thead>
          <tr className="text-left text-xs tracking-wide text-apagado uppercase">
            <th className="py-1 font-semibold">
              {tr('Mes', 'Month')}
              <NotaDeAyuda texto={A.mes} />
            </th>
            {medidas.map(m => (
              <th key={m} className="py-1 text-right font-semibold">
                {nombreMedida(m)}
                <NotaDeAyuda texto={AYUDA_MEDIDA_COMPARATIVO[m]!} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {p.porMes.map(x => (
            <tr key={x.mes} className="border-t border-linea/70">
              <td className="py-1">{etiquetaDe(x.mes)}</td>
              {medidas.map(m => (
                <td key={m} className={celda}>
                  {numero(x.proyectado[mirada][m], decimalesDe(m))}
                </td>
              ))}
            </tr>
          ))}
          <tr className="border-t border-linea font-semibold">
            <td className="py-1">{tr(`Total ${p.meses} meses`, `${p.meses}-month total`)}</td>
            {medidas.map(m => (
              <td key={m} className={celda}>
                {numero(p.total[mirada][m], decimalesDe(m))}
              </td>
            ))}
          </tr>
          {p.cierreDelAnio && (
            <tr className="border-t border-linea text-verde-oscuro">
              <td className="py-1">
                {tr(`Cierre ${anio} (lo que va + proyectado)`, `${anio} close (to date + projected)`)}
              </td>
              {medidas.map(m => (
                <td key={m} className={celda}>
                  {numero(p.cierreDelAnio![mirada][m], decimalesDe(m))}
                </td>
              ))}
            </tr>
          )}
        </tbody>
      </table>
    </section>
  );
}

function TablaPorExportador({c, mirada, medida}: {c: Comparativo; mirada: Mirada; medida: Medida}) {
  const d = decimalesDe(medida);
  const filas = c.porExportador.filter(
    f => f.actual[mirada][medida] || f.mismoPeriodoAnterior[mirada][medida] || f.anteriorCompleto[mirada][medida],
  );
  return (
    <div className="mt-4 overflow-x-auto rounded-lg border border-linea bg-white p-4">
      <h3 className="font-medium">
        {nombreMedida(medida)} {tr('por exportador', 'by exporter')}
      </h3>
      {!filas.length ? (
        <p className="mt-2 text-sm text-apagado">{tr('Sin datos en estos periodos.', 'No data in these periods.')}</p>
      ) : (
        <table className="mt-2 w-full text-sm">
          <thead>
            <tr className="text-left text-xs tracking-wide text-apagado uppercase">
              <th className="py-1 font-semibold">
                {tr('Exportador', 'Exporter')}
                <NotaDeAyuda texto={A.exportador} />
              </th>
              <th className="py-1 text-right font-semibold">
                {c.enCurso ? tr(`${c.anio} hasta hoy`, `${c.anio} to date`) : c.anio}
                <NotaDeAyuda texto={A.actual} />
              </th>
              {c.enCurso && (
                <>
                  <th className="py-1 text-right font-semibold">
                    {tr(`Mismo periodo ${c.anioAnterior}`, `Same period ${c.anioAnterior}`)}
                    <NotaDeAyuda texto={A.mismoPeriodo} />
                  </th>
                  <th className="py-1 text-right font-semibold">
                    {tr('Variación', 'Change')}
                    <NotaDeAyuda texto={A.variacion} />
                  </th>
                </>
              )}
              <th className="py-1 text-right font-semibold">
                {tr(`${c.anioAnterior} completo`, `Full ${c.anioAnterior}`)}
                <NotaDeAyuda texto={A.completo} />
              </th>
              <th className="py-1 text-right font-semibold">
                {c.enCurso ? tr('Avance', 'Reached') : tr('Variación', 'Change')}
                <NotaDeAyuda texto={c.enCurso ? A.avance : A.variacion} />
              </th>
            </tr>
          </thead>
          <tbody>
            {filas.map(f => {
              const actual = f.actual[mirada][medida];
              const mismo = f.mismoPeriodoAnterior[mirada][medida];
              const completo = f.anteriorCompleto[mirada][medida];
              return (
                <tr key={f.exportador} className="border-t border-linea/70">
                  <td className="py-1.5">
                    {f.exportador === 'Third Party' ? tr('De terceros', 'Third party') : f.exportador}
                  </td>
                  <td className="py-1.5 text-right tabular-nums">{numero(actual, d)}</td>
                  {c.enCurso && (
                    <>
                      <td className="py-1.5 text-right tabular-nums">{numero(mismo, d)}</td>
                      <td className="py-1.5 text-right tabular-nums">
                        <Variacion valor={variacion(actual, mismo)} />
                      </td>
                    </>
                  )}
                  <td className="py-1.5 text-right tabular-nums">{numero(completo, d)}</td>
                  <td className="py-1.5 text-right tabular-nums">
                    {c.enCurso ? (
                      completo > 0 ? (
                        `${numero((actual / completo) * 100, 0)} %`
                      ) : (
                        '—'
                      )
                    ) : (
                      <Variacion valor={variacion(actual, completo)} />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
